import React, { useRef, useState, useEffect } from "react";
import { FaPlay, FaPause } from "react-icons/fa";
import PlaybackWaveform from "./PlaybackWaveform";
import { parseVoiceWaveform } from "../utils/audioWaveform";

function shouldFetchAsBlob(url) {
  if (!url || url.startsWith("blob:")) return true;
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
}

export default function VoiceNote({ audioUrl, waveform, durationMs }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(() => (durationMs > 0 ? durationMs / 1000 : 0));
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const createdBlobUrlRef = useRef(null);

  useEffect(() => {
    setError(null);
    const revokeCreated = () => {
      const u = createdBlobUrlRef.current;
      if (u) {
        URL.revokeObjectURL(u);
        createdBlobUrlRef.current = null;
      }
    };

    const run = async () => {
      if (!audioUrl?.trim()) return;
      if (audioUrl.startsWith("blob:")) {
        revokeCreated();
        setObjectUrl(audioUrl);
        return;
      }
      if (!shouldFetchAsBlob(audioUrl)) {
        revokeCreated();
        setObjectUrl(null);
        return;
      }
      try {
        const response = await fetch(audioUrl, { credentials: "same-origin", cache: "no-store" });
        if (!response.ok) throw new Error("Failed to fetch audio");
        const blob = await response.blob();
        revokeCreated();
        const url = URL.createObjectURL(blob);
        createdBlobUrlRef.current = url;
        setObjectUrl(url);
      } catch (err) {
        console.error("Error fetching audio blob:", err);
        revokeCreated();
        setObjectUrl(null);
      }
    };

    void run();
    return () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(durationMs > 0 ? durationMs / 1000 : 0);
      setError(null);
      revokeCreated();
      setObjectUrl(null);
    };
  }, [audioUrl, durationMs]);

  const effectiveSrc = objectUrl ?? audioUrl;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handleLoaded = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0 && audio.duration !== Infinity) {
        setDuration(audio.duration);
      }
    };
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("loadedmetadata", handleLoaded);
    audio.addEventListener("durationchange", handleLoaded);
    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("loadedmetadata", handleLoaded);
      audio.removeEventListener("durationchange", handleLoaded);
    };
  }, [effectiveSrc]);

  useEffect(() => {
    if (!isPlaying) return undefined;
    let raf = 0;
    const tick = () => {
      const audio = audioRef.current;
      if (audio) {
        if (Number.isFinite(audio.currentTime)) setCurrentTime(audio.currentTime);
        if (Number.isFinite(audio.duration) && audio.duration > 0 && audio.duration !== Infinity) {
          setDuration(audio.duration);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    const play = audio.play();
    if (play && typeof play.then === "function") {
      play.then(() => setIsPlaying(true)).catch(() => {
        setIsPlaying(false);
        setError("Playback failed. Please try again.");
      });
    } else {
      setIsPlaying(true);
    }
  };

  const formatTime = (time) => {
    if (!Number.isFinite(time) || time < 0) time = 0;
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  return (
    <div className="flex min-w-[15.5rem] max-w-full flex-col gap-1">
      <div className="relative flex w-full min-w-[15.5rem] max-w-[min(100%,18rem)] items-center gap-2 rounded-xl bg-black px-2.5 py-2 shadow-md sm:max-w-md sm:gap-2.5 sm:px-3 sm:py-2.5">
        <button
          type="button"
          onClick={togglePlay}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-black transition-opacity hover:opacity-90"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? <FaPause className="h-3.5 w-3.5" /> : <FaPlay className="h-3.5 w-3.5 translate-x-px" />}
        </button>
        <PlaybackWaveform
          audioUrl={effectiveSrc}
          currentTime={currentTime}
          duration={duration}
          bars={parseVoiceWaveform(waveform) ?? waveform}
          onDecodedMeta={({ durationSec }) => {
            if (durationSec > 0) setDuration((prev) => (prev > 0 ? prev : durationSec));
          }}
          onSeekRatio={(ratio) => {
            const audio = audioRef.current;
            const len = duration > 0 ? duration : audio?.duration || 0;
            if (!audio || !Number.isFinite(len) || len <= 0) return;
            audio.currentTime = ratio * len;
            setCurrentTime(audio.currentTime);
          }}
        />
        <div className="w-10 shrink-0 text-right text-[11px] font-semibold tabular-nums text-white sm:text-xs">
          {isPlaying || currentTime > 0.4 ? formatTime(currentTime) : formatTime(duration)}
        </div>
      </div>
      <audio
        ref={audioRef}
        src={effectiveSrc}
        preload="metadata"
        hidden
        onLoadedMetadata={() => {
          const audio = audioRef.current;
          if (audio && !isNaN(audio.duration) && audio.duration !== Infinity) {
            setDuration(audio.duration);
          }
          setError(null);
        }}
        onTimeUpdate={() => {
          const audio = audioRef.current;
          if (audio) setCurrentTime(audio.currentTime);
        }}
        onEnded={() => setIsPlaying(false)}
        onError={() => setError("Could not load this voice note. Try again later.")}
      />
      {error ? <div className="text-xs text-red-400">{error}</div> : null}
    </div>
  );
}
